import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import InputGroup from '../../../../../../components/global/InputGroup';
import { getFormikTouchedErrors } from '../../../../../../util/formik';
import Alert from '../../../../../../components/global/Alert';
import BoxedCustomControl from '../../../../../../components/global/BoxedCustomControl';
import { sanitizeJoinCode } from '../../../../../../util/join-code';
import Button from '../../../../../../components/global/Button';
import { ALBUM_PRIVACY } from '../../../../../../constants/albums';
import DiamondUpgradeButton from '../../WeddingEventCreateWizard/components/DiamondUpgradeButton';

const propTypes = {
  values: PropTypes.shape({
    join_code: PropTypes.string,
    join_code_confirm: PropTypes.string,
    public_join_code_verification_enabled: PropTypes.bool,
    join_code_disabled: PropTypes.bool,
  }).isRequired,
  touched: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  joinCodeDisabled: PropTypes.bool,
  isCreate: PropTypes.bool,
  sampleJoinCode: PropTypes.string,
  upgradeRequired: PropTypes.bool,
  onUpgrade: PropTypes.func,
};

const AlbumFolderJoinCode = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  joinCodeDisabled,
  isCreate = true,
  sampleJoinCode = 'Woodstock69',
  upgradeRequired,
  onUpgrade,
}) => {
  const errorList = Object.values(getFormikTouchedErrors(errors, touched));

  useEffect(() => {
    if (values.join_code && values.join_code_disabled) {
      handleChange({
        target: {
          name: 'join_code_disabled',
          value: false,
        },
      });
    }
  }, [values.join_code, values.join_code_disabled]);

  const textClasses = classNames({
    'text-center': isCreate,
  });

  return (
    <Fragment>
      <div className="d-flex flex-column flex-sm-row">
        <InputGroup
          id="join-code"
          name="join_code"
          type="text"
          label={
            <span className="d-flex">
              Join Code
              {upgradeRequired && onUpgrade && (
                <DiamondUpgradeButton onUpgrade={onUpgrade} />
              )}
            </span>
          }
          labelType="expanded"
          placeholder={`e.g. "${sampleJoinCode}"`}
          value={values.join_code}
          onChange={event => {
            const { target } = event;
            const { value } = target;
            handleChange({
              target: {
                name: 'join_code',
                value: sanitizeJoinCode(value),
              },
            });
          }}
          addon="#"
          wrapperClassName="w-100 mb-1 mb-sm-0"
          disabled={joinCodeDisabled}
          onBlur={handleBlur}
        />

        <InputGroup
          id="join-code-confirm"
          name="join_code_confirm"
          type="text"
          label={
            <span className="d-flex">
              Confirm Join Code
              {upgradeRequired && onUpgrade && (
                <DiamondUpgradeButton onUpgrade={onUpgrade} />
              )}
            </span>
          }
          labelType="expanded"
          placeholder="Re-enter join code"
          value={values.join_code_confirm}
          onChange={event => {
            const { target } = event;
            const { value } = target;
            handleChange({
              target: {
                name: 'join_code_confirm',
                value: sanitizeJoinCode(value),
              },
            });
          }}
          addon="#"
          onBlur={handleBlur}
          wrapperClassName="w-100 mb-0 ml-0 ml-sm-1"
          disabled={joinCodeDisabled}
        />
      </div>

      {values.join_code && !joinCodeDisabled && (
        <div className="text-right mt-1">
          <Button
            btnSize="xs"
            btnStyle="link"
            checked={values.join_code_disabled === true}
            onClick={() => {
              handleChange({
                target: {
                  name: 'join_code_disabled',
                  value: !values.join_code_disabled,
                },
              });
              handleChange({
                target: {
                  name: 'join_code',
                  value: '',
                },
              });
              handleChange({
                target: {
                  name: 'join_code_confirm',
                  value: '',
                },
              });
              handleChange({
                target: {
                  name: 'public_join_code_verification_enabled',
                  value: false,
                },
              });
            }}
          >
            Clear All
          </Button>
        </div>
      )}

      {values.join_code &&
        values.privacy === ALBUM_PRIVACY.PUBLIC &&
        (joinCodeDisabled || values.join_code === values.join_code_confirm) && (
          <Fragment>
            <p className={`${textClasses} mt-1`}>
              Require Join Code for viewing your gallery via Waldo search or
              your profile page:
            </p>
            <BoxedCustomControl
              id="public_join_code_verification_enabled"
              name="public_join_code_verification_enabled"
              type="checkbox"
              label="Yes, require code for access"
              className="text-sm text-bold"
              checked={values.public_join_code_verification_enabled}
              onChange={() => {
                handleChange({
                  target: {
                    name: 'public_join_code_verification_enabled',
                    value: !values.public_join_code_verification_enabled,
                  },
                });
              }}
            />
          </Fragment>
        )}

      {errorList.length > 0 && (
        <Alert type="danger" className="mt-1">
          <p className="text-bold">Some issues with your data:</p>
          <ul className="mb-0">
            {errorList.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Fragment>
  );
};

AlbumFolderJoinCode.propTypes = propTypes;

export default AlbumFolderJoinCode;
